import { useEffect, useMemo, useRef, useState } from 'react';

import { useLazyQuery, useQuery } from '@apollo/client/react';
import * as Sentry from '@sentry/react';

import { get as getUser } from 'js/lib/user';

import AssignmentPassingThresholdQuery from 'bundles/assess-common/api/AssignmentPassingThreshold.graphql';
import GetSkillAreaCourseItemsQuery, {
  type GetSkillAreaCourseItemsQueryData,
  type GetSkillAreaCourseItemsQueryVariables,
} from 'bundles/assess-common/api/GetSkillAreaCourseItems';
import type {
  AssignmentPassingThresholdQuery as AssignmentPassingThresholdQueryData,
  AssignmentPassingThresholdQueryVariables,
} from 'bundles/assess-common/api/__generated__/AssignmentPassingThreshold';
import { getSkillPassingThresholdForAssessment } from 'bundles/assess-common/utils/skillAssessmentsUtils';
import GetSkillExpressionAssessmentMappingsByAssessmentIdQuery from 'bundles/skills-common/shared/verified-skills/data/skill-pathway/GetSkillExpressionAssessmentMappingsByAssessmentId.graphql';
import type { SkillAttainmentSkillAreaAttainmentFragment } from 'bundles/skills-common/shared/verified-skills/data/skill-pathway/__generated__/GetSkillAttainmentByOccupationGroupAndCareerBandQuery';
import type {
  GetSkillExpressionAssessmentMappingsByAssessmentIdQuery as GetSkillExpressionAssessmentMappingsByAssessmentIdQueryData,
  GetSkillExpressionAssessmentMappingsByAssessmentIdQueryVariables,
} from 'bundles/skills-common/shared/verified-skills/data/skill-pathway/__generated__/GetSkillExpressionAssessmentMappingsByAssessmentId';

export type SkillPassingThresholdTotals = {
  hasMasteredSkillArea: boolean;
  skillAttainmentThreshold: number;
};

type Props = {
  skillAttainmentForCourse?: SkillAttainmentSkillAreaAttainmentFragment;
  courseId?: string;
};

/**
 * Custom hook that calculates the total and passing threshold for skill assessments.
 * Fetches thresholds for all assessments and calculates aggregated totals using
 * getSkillPassingThresholdForAssessment.
 * Use this hook when you need to fetch mastery details for a skill area with some already mastered skills. No mastered skills means the course might not be enrolled in yet.
 *
 * @param skillAttainmentForCourse - The skill attainment for the course.
 * @param courseId - The course ID.
 * @returns The skill mastery details.
 */
export const useGetSkillMasteryDetailsForSkillArea = ({ skillAttainmentForCourse, courseId }: Props) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [totals, setTotals] = useState<SkillPassingThresholdTotals>({
    hasMasteredSkillArea: false,
    skillAttainmentThreshold: 0,
  });

  const userId = getUser().id;

  const {
    data,
    error: itemsError,
    loading: itemsLoading,
  } = useQuery<GetSkillAreaCourseItemsQueryData, GetSkillAreaCourseItemsQueryVariables>(GetSkillAreaCourseItemsQuery, {
    variables: {
      courseId: courseId || '',
      learnerId: `${userId}` || '',
    },
    fetchPolicy: 'cache-first',
  });

  const items = data?.OnDemandCourseMaterialsV2?.learner?.linked?.onDemandCourseMaterialItemsV2;

  const skillAssessmentItemIds = useMemo(
    () =>
      items
        ?.filter(
          (item) =>
            item?.contentSummary?.typeName === 'staffGraded' &&
            item?.contentSummary?.definition?.assignmentFeatures?.includes('VERIFIED_SKILLS_ASSESSMENT')
        )
        .map((item) => item?.id || '') || [],
    [items]
  );

  const [fetchPassingThreshold] = useLazyQuery<
    AssignmentPassingThresholdQueryData,
    AssignmentPassingThresholdQueryVariables
  >(AssignmentPassingThresholdQuery, {
    context: { clientName: 'gatewayGql' },
    fetchPolicy: 'cache-first',
  });

  const [fetchSkillExpressionMappings] = useLazyQuery<
    GetSkillExpressionAssessmentMappingsByAssessmentIdQueryData,
    GetSkillExpressionAssessmentMappingsByAssessmentIdQueryVariables
  >(GetSkillExpressionAssessmentMappingsByAssessmentIdQuery, {
    context: { clientName: 'gatewayGql' },
    fetchPolicy: 'cache-first',
  });

  // Instead of disabling the rule entirely, be explicit about stable references
  const fetchPassingThresholdRef = useRef(fetchPassingThreshold);
  const fetchSkillExpressionMappingsRef = useRef(fetchSkillExpressionMappings);

  useEffect(() => {
    fetchPassingThresholdRef.current = fetchPassingThreshold;
    fetchSkillExpressionMappingsRef.current = fetchSkillExpressionMappings;
  });

  useEffect(() => {
    let isCancelled = false;

    const fetchAllPassingThresholds = async () => {
      // Don't set loading to false if the items query is still loading
      // because we don't know yet if there will be items
      if (itemsLoading) {
        return;
      }

      if (!skillAssessmentItemIds || skillAssessmentItemIds.length === 0) {
        setLoading(false);
        return;
      }

      setLoading(true);

      try {
        // Fetch passing thresholds first, then use assignment.id to fetch skill expression mappings
        const results = await Promise.all(
          skillAssessmentItemIds.map(async (itemId: string) => {
            const passingThresholdResult = await fetchPassingThresholdRef.current({
              variables: {
                itemId,
                courseId: courseId || '',
              },
            });

            if (
              passingThresholdResult.error ||
              passingThresholdResult.data?.SubmissionState?.queryState?.__typename !== 'Submission_SubmissionState'
            ) {
              throw new Error('Failed to fetch passing threshold');
            }

            const queryState = passingThresholdResult.data?.SubmissionState?.queryState;
            const assignmentState =
              queryState?.__typename === 'Submission_SubmissionState' ? queryState.assignment : null;

            const assessmentId = assignmentState?.id?.split('@')[0] || '';

            const skillExpressionsMappingResult = assessmentId
              ? await fetchSkillExpressionMappingsRef.current({
                  variables: {
                    assessmentId,
                    assessmentType: 'assignment',
                  },
                })
              : null;

            return {
              itemId,
              passingFraction: assignmentState?.passingFraction || 1,
              skillExpressionMappings:
                skillExpressionsMappingResult?.data?.VerifiedSkillsMapping
                  ?.getSkillExpressionAssessmentMappingsByAssessmentId,
            };
          })
        );

        if (isCancelled) return;

        // Calculate totals for each assessment and aggregate
        const calculatedTotals = results.reduce<SkillPassingThresholdTotals>(
          (acc, { passingFraction, skillExpressionMappings }, index) => {
            const mappedSkillExpressionIds = new Set(
              skillExpressionMappings?.map((mapping) => mapping.skillExpression.id) ?? []
            );

            const skillExpressionsAttainment =
              skillAttainmentForCourse?.skillExpressionsAttainment?.filter((attainment) =>
                mappedSkillExpressionIds.has(attainment?.skillExpression?.id ?? '')
              ) || [];

            const assessmentResult = getSkillPassingThresholdForAssessment({
              skillExpressionsAttainment,
              passingFraction,
            });

            return {
              hasMasteredSkillArea:
                index === 0 ? assessmentResult.isPassed : acc.hasMasteredSkillArea && assessmentResult.isPassed,
              skillAttainmentThreshold: acc.skillAttainmentThreshold + assessmentResult.skillAttainmentThreshold,
            };
          },
          {
            hasMasteredSkillArea: false,
            skillAttainmentThreshold: 0,
          }
        );

        setTotals(calculatedTotals);
      } catch (err) {
        setError(err as Error);
        Sentry.captureException(err as Error, {
          extra: {
            message: 'Failed to fetch skill mastery details and passing thresholds for skill area',
            courseId,
            skillAreaId: skillAttainmentForCourse?.skillArea?.id,
          },
        });
      } finally {
        if (!isCancelled) {
          setLoading(false);
        }
      }
    };

    fetchAllPassingThresholds();

    return () => {
      isCancelled = true;
    };
  }, [courseId, skillAttainmentForCourse, itemsLoading, skillAssessmentItemIds]);

  const total = skillAssessmentItemIds?.length;
  const isLoading = itemsLoading || loading;

  return {
    total,
    loading: isLoading,
    error: itemsError || error,
    hasMasteredSkillArea: isLoading ? false : totals.hasMasteredSkillArea,
    skillAttainmentThreshold: isLoading ? 0 : totals.skillAttainmentThreshold,
  };
};
